import { Component, type EventEmitter, Event, Prop, h } from '@stencil/core';
import { GenericCardTitle } from '../../../../new-result-cards/generic-subcomponents/generic-card-title/generic-card-title';
import type { SearchResultClickEvent } from '../../../SearchResultClickEvent';
import { Strategies } from '../../../../../strategies/Strategies';
import { DashboardOrderItemLabels } from './dashboard-order-item-labels';
import { DashboardQuoteItemLabels } from './dashboard-quote-item-labels';
import type { SearchApiDocumentMySE } from '../../../../../../api/model/response/documents/private/MySEDocument';
import type {SearchProduct} from '../../../../../../api/model/response/documents/public/Product';
import {ResultCardType} from '../../../../new-result-cards/public/product-card/helpers/helpers';
import {
  GenericCardEventsLayer
} from '../../../../new-result-cards/generic-subcomponents/generic-card-event-layer/generic-card-event-layer';
import { DashboardTopicItemLabels } from './dashboard-topic-item-labels';
import { GenericCardThumbnail } from '../../../../new-result-cards/generic-subcomponents/generic-card-thumbnail/generic-card-thumbnail';
import { UiaasSearchLabelList } from '../../../../result-cards/subcomponents/uiaas-search-label-list';
import { getTrainingThumbnail } from '../../utils/utils';
import { DashboardCpqQuoteItemLabels } from './dashboard-cpq-quote-item-labels';
import type { labelProviderInterface } from '../../../../../LabelProviderInterface';

@Component({
  tag: 'dashboard-private-item',
  shadow: false,
  styleUrl: 'dashboard-private-tile.scss',
})
export class DashboardPrivateItem {
  @Prop() readonly doc: SearchApiDocumentMySE;
  @Prop() readonly index: number;
  @Prop({mutable: true}) labelProvider?: labelProviderInterface;
  @Prop() readonly strategies: Strategies;
  @Prop() readonly section: string;
  @Prop() readonly showPlaceholder?: boolean;
  @Prop() readonly rtl?: boolean;

  @Event({
    eventName: 'search:ResultCardClick',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    resultCardClickEventEmitter: EventEmitter<SearchResultClickEvent>;

  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (fallback) => {
            return fallback;
        };
      }
  }

  private mapQuoteStatusToColor = () => {
      switch(this.doc?.status?.trim?.()) {
      case 'Created':
      case 'BeingProcessed':
      case 'RO':
      case '07':
      case '09':
      case 'OP':
      case 'VA':
        return 'success';
      case 'PartiallyRejected':
      case 'Submitted':
      case 'QZDP':
      case '01':
      case '02':
      case '03':
      case 'O':
      case '08':
      case 'SA':
      case 'UC':
      case 'AP':
      case 'FO':
      case 'Fully Ordered':
        return 'warning';
      case 'QAQT':
      case 'QZWN':
      case '04':
      case '05':
      case '06':
      case '12':
      case '13':
      case 'Completed':
      case 'Rejected':
      case 'RE':
      case 'CO':
      case 'R':
      case 'E':
        return 'neutral';
      default:
        return 'neutral';
      }
  };

  private mapOrderStatusToColor = () => {
      switch(this.doc?.status?.trim?.()) {
      case 'Open':
        return 'success';
      case 'In Process':
      case 'In Progress':
      case 'Partially Shipped':
      case 'Submitted':
      case 'Invoiced':
        return 'warning';
      default:
        return 'neutral';
      }
  };

  render() {
      const showStatus = (this.section === 'quotes') || (this.section === 'orders' && this.doc.status);
      const showImage = ['topics', 'trainings'].includes(this.section);
      const image = this.section === 'trainings' ? getTrainingThumbnail(this.doc.trainingType) : this.doc.thumbnailUrl;
      let itemStatusKey = this.doc?.status;
      let header = this.doc?.title;
      let status = this.doc?.status;

      if(this.section === 'orders'){
        status = this.mapOrderStatusToColor();
        if(itemStatusKey != null && itemStatusKey != undefined)
        {
          const trimmedStatus = itemStatusKey.replace(',', '').replace(/\s/g, '');
          itemStatusKey = `orders.status.${trimmedStatus}`;
        }
        if (!header) {
          header = this.doc.referenceNumber;
        }
      }

      if(this.section === 'quotes'){
        status = this.mapQuoteStatusToColor();
        header = this.doc?.quotationNumber || this.doc?.quoteNumber;
        const trimmedStatus = itemStatusKey.replace(',', '').replace(/\s/g, '');
        itemStatusKey = `quotes.facet.status.${trimmedStatus.toUpperCase()}`;
      }

      if(this.section === 'topics'){
        header = this.doc?.title;
      }

      return (
        <GenericCardEventsLayer
          clickEmitter={this.resultCardClickEventEmitter}
          doc={this.doc as unknown as SearchProduct}
          resutCardType={ResultCardType.Basic}
          index={this.index}
          section={this.section}
        >
          <div class="private__tile">
            <a href={this.doc.url} data-card-area={'background'} target="_blank">
              {showImage ?
                <GenericCardThumbnail image={image} />
                : '' }
            </a>
            <div class="private__content">
              <a href={this.doc?.url} data-card-area={'background'}>
                <GenericCardTitle
                  url={this.doc?.url}
                  text={header}
                  strategies={this.strategies}
                />
                <div class="private__label-panel" tabindex="0">
                  {this.section === 'orders' && header ?
                    <DashboardOrderItemLabels
                      doc={this.doc}
                      labelProvider={this.labelProvider}
                    />
                    : ''}
                  {this.section === 'quotes' && this.doc?.source == 'Quotes' ?
                    <DashboardQuoteItemLabels
                      doc={this.doc}
                      labelProvider={this.labelProvider}
                    />
                    : ''}
                  {this.section === 'quotes' && this.doc?.source == 'QuotesCPQ' ?
                    <DashboardCpqQuoteItemLabels
                      doc={this.doc}
                      labelProvider={this.labelProvider}
                    />
                    : ''}
                  {this.section === 'topics' ?
                    <DashboardTopicItemLabels
                      doc={this.doc}
                      labelProvider={(e, f) => this.labelProvider(e, f)}
                    />
                    : ''}
                  {this.section === 'trainings' ?
                    <UiaasSearchLabelList
                      list={[
                        {
                          label: this.labelProvider('tab.label.duration', 'Duration'),
                          value: `${this.doc?.duration} ${this.labelProvider('tab.label.minutes', 'minutes')}`,
                        },
                        {
                          value: this.doc?.courseCode,
                        },{
                          value: this.doc?.trainingType,
                          customValueClass: 'document__category',
                        },
                      ]}
                    />
                    : ''}
                </div>
              </a>
              <div class="private__label-container">
                {showStatus ?
                  <qds-tag
                    class="orders"
                    text={this.labelProvider(itemStatusKey, this.doc?.status)}
                    status={status}
                    size="small"
                  />
                  : ''}
              </div>
            </div>
          </div>
        </GenericCardEventsLayer>
      );
  }
}
